import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/HrInductionTrainingSheetLayout.css";

const pickValue = (source, keys, fallback = "") => {
	if (!source) return fallback;

	for (const key of keys) {
		const value = source[key];
		if (value !== undefined && value !== null && String(value).trim() !== "") {
			return value;
		}
	}

	return fallback;
};

const isMeaningful = (value) => {
	if (value === undefined || value === null) return false;
	const normalized = String(value).trim().toLowerCase();
	return !["", "-", "n/a", "na", "null", "undefined"].includes(normalized);
};

const pickByKeyIncludes = (source, includes = []) => {
	if (!source || !includes.length) return "";
	const entries = Object.entries(source);
	for (const [key, value] of entries) {
		const normalizedKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
		const hasAll = includes.every((token) => normalizedKey.includes(token));
		if (hasAll && isMeaningful(value)) {
			return value;
		}
	}
	return "";
};

const formatDate = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);

	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();
	return `${day}-${month}-${year}`;
};

const toTitleFromCode = (value = "") =>
	String(value)
		.split("_")
		.filter(Boolean)
		.map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
		.join(" ");

const toSlug = (value = "") =>
	String(value)
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const sanitizeReportTitle = (value = "") => {
	const title = String(value || "").trim();
	if (!title) return "";
	const compact = title.replace(/\s+/g, "");
	if (compact.length <= 4 && !title.includes(" ")) {
		return "";
	}
	return title;
};

const resolveReportTitle = (headDetails = {}) => {
	const directTitle = sanitizeReportTitle(
		pickValue(headDetails, [
			"report_title",
			"form_title",
			"title",
			"transaction_name",
			"tran_name",
		]),
	);

	if (directTitle) {
		return directTitle;
	}

	const codeTitle = sanitizeReportTitle(
		toTitleFromCode(pickValue(headDetails, ["transaction_code", "tran_code"])) ||
			toTitleFromCode(pickValue(headDetails, ["transactionCode"])),
	);

	if (codeTitle) {
		return codeTitle;
	}

	return "INDUCTION TRAINING SHEET";
};

const resolveRows = (itemDetails = [], headDetails = {}) => {
	const rows = (Array.isArray(itemDetails) ? itemDetails : [])
		.map((item, index) => ({
			sl_no: pickValue(item, ["sl_no", "slno", "serial_no"], index + 1),
			department: pickValue(item, ["department", "dept", "name_of_department"]),
			trainer_sign: pickValue(item, [
				"trainer_sign",
				"trainer_sighn",
				"trainer_signature",
				"trainer_signoff",
			]),
			trainee_sign: pickValue(item, [
				"trainee_sign",
				"trainee_sighn",
				"trainee_signature",
				"employee_signature",
				"signature",
			]),
		}))
		.filter(
			(row) =>
				isMeaningful(row.department) ||
				isMeaningful(row.trainer_sign) ||
				isMeaningful(row.trainee_sign),
		);

	if (rows.length === 0) {
		const headRow = {
			sl_no: 1,
			department: pickValue(headDetails, ["department", "dept", "name_of_department"]),
			trainer_sign: pickValue(headDetails, [
				"trainer_sign",
				"trainer_sighn",
				"trainer_signature",
				"trainer_signoff",
			]),
			trainee_sign: pickValue(headDetails, [
				"trainee_sign",
				"trainee_sighn",
				"trainee_signature",
				"employee_signature",
				"signature",
			]),
		};

		const hasHeadData =
			isMeaningful(headRow.department) ||
			isMeaningful(headRow.trainer_sign) ||
			isMeaningful(headRow.trainee_sign);

		if (hasHeadData) {
			rows.push(headRow);
		}
	}

	return rows;
};

export const HrInductionTrainingSheetLayout = ({
	headDetails = {},
	companyData = {},
	itemDetails = [],
}) => {
	let organisationData = {};
	try {
		organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
	} catch (error) {
		organisationData = {};
	}

	const firstItem = (Array.isArray(itemDetails) && itemDetails[0]) || {};

	const refNo = pickValue(headDetails, ["ref_no", "reference_no", "form_ref_no"]);
	const revNo = pickValue(headDetails, ["rev_no", "revision_no", "form_rev_no"]);
	const revDate = formatDate(
		pickValue(headDetails, ["rev_date", "revision_date", "form_rev_date"]),
	);

	const employeeName =
		pickValue(
			headDetails,
			[
				"name_of_emp",
				"name_of_employee",
				"name_of_employe",
				"name_of_employeee",
				"employee_name",
				"emp_name",
				"employee",
				"trainee_name",
				"name",
			],
			"",
		) ||
		pickByKeyIncludes(headDetails, ["employee", "name"]) ||
		pickValue(firstItem, [
			"name_of_emp",
			"name_of_employee",
			"name_of_employe",
			"name_of_employeee",
			"employee_name",
			"emp_name",
			"employee",
			"trainee_name",
			"name",
		]) ||
		pickByKeyIncludes(firstItem, ["name", "employee"]) ||
		pickByKeyIncludes(headDetails, ["name"]) ||
		pickByKeyIncludes(firstItem, ["employee", "name"]);
	const departmentName = pickValue(headDetails, [
		"name_of_department",
		"department_name",
		"department",
		"dept",
	]);
	const dateOfJoin = formatDate(
		pickValue(headDetails, [
			"date",
			"date_of_join",
			"date_of_joining",
			"joining_date",
			"doj",
		]),
	);
	const trainingDate = formatDate(
		pickValue(headDetails, ["training_date", "trainingdate", "created_at"]),
	);

	const reviewedBy = pickValue(headDetails, ["reviewed_by"]);
	const reviewedSignDate = formatDate(
		pickValue(headDetails, ["reviewed_sign_date", "reviewed_date"]),
	);

	const preparedBy = pickValue(headDetails, ["prepared_by", "created_by"]);
	const preparedSignDate = formatDate(
		pickValue(headDetails, ["prepared_sign_date", "prepared_date"]),
	);

	const approvedBy = pickValue(headDetails, ["reviewed_and_approved_by", "approved_by"]);
	const approvedSignDate = formatDate(
		pickValue(headDetails, ["approved_sign_date", "approved_date"]),
	);

	const pageNo = pickValue(headDetails, ["page_no"], "1 of 1");
	const reportTitle = resolveReportTitle(headDetails);
	const reportFileName =
		pickValue(headDetails, ["download_file_name", "report_file_name", "file_name"]) ||
		toSlug(reportTitle) ||
		"manual-report";

	const rows = resolveRows(itemDetails, headDetails);

	const tableData = {
		headings: [
			{ display_name: "SL.NO", field_name: "sl_no", is_internal: false },
			{ display_name: "DEPARTMENT", field_name: "department", is_internal: false },
			{
				display_name: "TRAINER SIGN",
				field_name: "trainer_sign",
				is_internal: false,
			},
			{
				display_name: "TRAINEE SIGN",
				field_name: "trainee_sign",
				is_internal: false,
			},
		],
		rows,
	};

	return (
		<div className="hr-induction-page">
			<div className="hr-induction-card">
				<div className="hr-induction-header-row">
					<div className="hr-induction-brand">
						<img
							src={organisationData?.logo_url || ProfilePlaceholder}
							alt="Company Logo"
							className="hr-induction-logo"
						/>
						<div className="hr-induction-org-name">
							{organisationData?.name || companyData?.company_name || ""}
						</div>
					</div>
					<div className="hr-induction-title-box">{reportTitle}</div>
					<div className="hr-induction-ref-box">
						<div>REF. NO.: {refNo || "-"}</div>
						<div>REV. NO.: {revNo || "-"}</div>
						<div>REV. DATE: {revDate || "-"}</div>
					</div>
				</div>

				<table className="hr-induction-detail-table">
					<tbody>
						<tr>
							<td className="label-cell">Name of Employee</td>
							<td colSpan={3}>{employeeName}</td>
						</tr>
						<tr>
							<td className="label-cell">Name of the Department</td>
							<td colSpan={3}>{departmentName}</td>
						</tr>
						<tr>
							<td className="label-cell">Date of Join</td>
							<td>{dateOfJoin}</td>
							<td className="label-cell">Training Date</td>
							<td>{trainingDate}</td>
						</tr>
						<tr className="table-head-row">
							<td className="sl-col">Sl. No.</td>
							<td className="dept-col">Department</td>
							<td className="sign-col">Trainer Sign</td>
							<td className="sign-col">Trainee sign</td>
						</tr>
						{rows.map((row) => (
							<tr key={`hr-induction-row-${row.sl_no}`}>
								<td className="sl-col">{row.sl_no}</td>
								<td className="dept-col">{row.department}</td>
								<td className="sign-col">{row.trainer_sign}</td>
								<td className="sign-col">{row.trainee_sign}</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className="hr-induction-review-box">
					<div>Reviewed by: {reviewedBy}</div>
					<div>Sign & Date: {reviewedSignDate}</div>
				</div>

				<div className="hr-induction-footer-row">
					<div className="footer-box">
						<div>Prepared By: {preparedBy}</div>
						<div>Sign & Date: {preparedSignDate}</div>
					</div>
					<div className="footer-box">
						<div>Reviewed & Approved By: {approvedBy}</div>
						<div>Sign & Date: {approvedSignDate}</div>
					</div>
					<div className="footer-box page-box">Page No.: {pageNo}</div>
				</div>

				<p className="invoice-system-generated-note">
					This is a system-generated document and does not require a signature.
				</p>

				<div className="invoice-buttons hr-induction-actions">
					<DownloadButton
						tableData={tableData}
						fileName={reportFileName}
						downloadFormats={["pdf", "full-page-pdf"]}
						fullPageDownload={true}
						fullPageSelector=".hr-induction-page"
					/>
				</div>
			</div>
		</div>
	);
};
